import { type ReactNode } from 'react'

import type { HospitalListItem } from '../api/hospitals'
import { bedTone } from '../lib/hospitalCard'
import { cn } from '../lib/cn'
import type { BedTone } from './BedBadge'

export interface MapPlaceholderProps {
  hospitals: HospitalListItem[]
  onSelect?: (slug: string) => void
  className?: string
}

// Solid pin colours per ICU-availability tone (colour is paired with the
// numeric free count on the pin — never colour alone).
const PIN_BG: Record<BedTone, string> = {
  good: 'bg-bed-good',
  warn: 'bg-bed-warn',
  bad: 'bg-bed-bad',
}

/** Normalize a value into 0..1 within [min,max]; midpoint when the span is 0. */
function norm(value: number, min: number, max: number): number {
  return max > min ? (value - min) / (max - min) : 0.5
}

/**
 * Placeholder for the real tiled Google Maps view (M3-T09). Plots the
 * hospitals by their relative lat/lng — a truthful relative layout, not fake
 * pins — so it stays meaningful until the map SDK lands. Each pin's colour is
 * its ICU-availability tone and its label the free count.
 */
export function MapPlaceholder({
  hospitals,
  onSelect,
  className,
}: MapPlaceholderProps): ReactNode {
  const lats = hospitals.map((h) => h.lat)
  const lngs = hospitals.map((h) => h.lng)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)

  return (
    <div
      role="group"
      aria-label="Hospital map"
      className={cn(
        // M17-T07 — was a hardcoded rounded-[10px], correct only while
        // --radius-r3 was 10px. T03 moved that token to 16px.
        'relative overflow-hidden rounded-r3 border border-line-1 bg-bg-sunken',
        // A subtle grid so it reads as a map surface.
        '[background-image:repeating-linear-gradient(0deg,transparent_0_39px,var(--color-line-1)_39px_40px),repeating-linear-gradient(90deg,transparent_0_39px,var(--color-line-1)_40px)]',
        className,
      )}
    >
      <span className="pointer-events-none absolute left-2 top-2 rounded bg-bg-app/90 px-2 py-1 font-mono text-[10px] text-ink-3">
        map placeholder · Google Maps pending
      </span>

      {/* Inset so pins near the edges aren't clipped. */}
      <div className="absolute inset-6">
        {hospitals.map((h) => {
          const tone = bedTone(h.beds.icu_free, h.beds.icu_total)
          const x = norm(h.lng, minLng, maxLng) * 100
          const y = (1 - norm(h.lat, minLat, maxLat)) * 100 // north is up
          return (
            <button
              key={h.id}
              type="button"
              onClick={() => onSelect?.(h.slug)}
              aria-label={`${h.name} — ICU ${h.beds.icu_free} free`}
              title={h.name}
              style={{ left: `${x}%`, top: `${y}%` }}
              className={cn(
                'absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-white shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                PIN_BG[tone],
              )}
            >
              {h.beds.icu_free}
            </button>
          )
        })}
      </div>
    </div>
  )
}
